import { useMemo } from 'react';
import { getAPN, loadAPN } from "../apn/APN.js";
import { DEFAULT_COUNTRY, getCountryCode, toCountryISO } from "../country-code-metadata/CountryCodeMetadata.js";
var PLUS_SIGN = '+';
var NON_PHONE_SYMBOLS_PATTERN = /[^+\d]/g;
var PHONE_PATTERN = /^\+?\d+/g;
function getPrefix(country) {
  return PLUS_SIGN + String(getCountryCode(toCountryISO(country)));
}
function trim(input) {
  return typeof input == 'string' ? input.trim() : undefined;
}
function normalize(input) {
  if (typeof input == 'string') {
    var matches = input.replace(NON_PHONE_SYMBOLS_PATTERN, '').match(PHONE_PATTERN);
    if (matches !== null && matches !== void 0 && matches[0]) return matches[0];
  }
  return '';
}
function normalizeNationalNumber(country, input) {
  var phone = normalize(input);
  var prefix = getPrefix(country);
  if (phone.startsWith(PLUS_SIGN)) {
    return phone.slice(prefix.length);
  }
  return phone;
}
export class PhoneService {
  static load() {
    return loadAPN().then(APN => new PhoneService(APN));
  }
  static checkPossibility(input) {
    return this.load().then(phoneService => phoneService.checkPossibility(input));
  }
  static validate(input, rules) {
    return this.load().then(phoneService => phoneService.validate(input, rules));
  }
  constructor(APN) {
    this.APN = APN;
  }
  createAPN(phone, country) {
    var asYouType = this.APN.getAsYouType(toCountryISO(country));
    asYouType.reset(normalize(phone));
    return asYouType.getPhoneNumber();
  }
  getJSON(phone, country) {
    return this.createAPN(phone, country);
  }
  checkPossibility(input) {
    var phone = trim(input);
    if (!phone) {
      return 'unknown';
    }
    var apn = phone.startsWith(PLUS_SIGN) ? this.APN.parsePhoneNumber(phone) : this.APN.parsePhoneNumber(phone, {
      regionCode: DEFAULT_COUNTRY
    });
    var {
      valid,
      possible,
      possibility
    } = apn;

    // Avoid false positive short phone numbers.
    if (!valid && possible) {
      return 'invalid-number';
    }
    return possibility;
  }
  validate(input) {
    var {
      required,
      requiredMessage = 'This field is required',
      invalidMessage = 'Invalid phone number',
      tooLongMessage = 'Phone number is too long',
      tooShortMessage = 'Phone number is too short'
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var phone = trim(input);
    if (!phone) {
      if (required) {
        return requiredMessage;
      }
      return undefined;
    }
    switch (this.checkPossibility(phone)) {
      case 'is-possible':
        return undefined;
      case 'too-long':
        return tooLongMessage;
      case 'too-short':
        return tooShortMessage;
    }
    return invalidMessage;
  }
  deletePrefix(phone, country) {
    var prefix = getPrefix(country);
    if (phone.startsWith(PLUS_SIGN)) {
      var subNumber = phone.slice(prefix.length);
      return trim(subNumber);
    }
    return phone;
  }
  getInfo(phone) {
    var {
      regionCode,
      number: {
        input,
        international: internationalNumber
      }
    } = this.getJSON(phone);
    var nationalNumber = '';
    var country = toCountryISO(regionCode);
    if (!internationalNumber) {
      nationalNumber = normalizeNationalNumber(country, input);
    } else {
      nationalNumber = this.deletePrefix(internationalNumber, country);
    }
    return {
      country,
      nationalNumber
    };
  }
  format(input) {
    var _apn$number;
    var {
      fallback = '',
      format = 'e164',
      country: countryOption
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var phone = normalize(input);
    if (!phone) {
      return fallback;
    }
    var apn = this.APN.parsePhoneNumber(phone, {
      regionCode: countryOption
    });
    var country = countryOption || toCountryISO(apn.regionCode);
    var international = (_apn$number = apn.number) === null || _apn$number === void 0 ? void 0 : _apn$number.international;
    var formattedNumber = apn.number ? apn.number[format] : '';
    var {
      regionCode
    } = apn;
    var formatted = format === 'national' ? this.formatNationalNumber(country, international) : formattedNumber;
    if (!formatted) {
      return this.customFormat(phone, format, regionCode);
    }
    return formatted;
  }
  formatNationalNumber(country, internationalNumber) {
    if (!internationalNumber) {
      return undefined;
    }
    return this.deletePrefix(internationalNumber, country);
  }
  customFormat(phone, format, regionCode) {
    var formatted = '';
    var country = toCountryISO(regionCode);
    var nationalNumber = normalizeNationalNumber(country, phone);
    if (format === 'national') {
      return nationalNumber;
    }
    var prefix = '';
    var separator = '';
    if (format === 'rfc3966') {
      prefix = 'tel:';
      separator = '-';
    }
    if (format === 'international') {
      separator = ' ';
    }
    formatted = prefix + getPrefix(country);
    if (nationalNumber) {
      formatted += separator + nationalNumber;
    }
    return formatted;
  }
}
export function usePhoneService() {
  var APN = getAPN();
  return useMemo(() => new PhoneService(APN), [APN]);
}